import { type ProjectData } from '$/pages/CustomEditorPage/utils/migrations';

const NON_BOOLEAN_TYPES = new Set([
  'grid',
  'edgeGrid',
  'retouchBrush',
  'retouchGradient',
  'retouchPattern',
  'cloneStamp',
  'colorChecker',
  'image',
]);

/**
 * Repairs corrupted project data before it is loaded into the editor.
 *
 * This sometimes happens during development and can cause long troubleshooting sessions.
 */
export const repairProjectData = ({
  views = [],
  ...rest
}: ProjectData): ProjectData => {
  const repairedViews = views.map(({ elements, components, ...rest }) => {
    const elementMap = new Map(elements.map((el) => [el.id, el]));

    // Fix 1: move misplaced booleans to their own component

    const repairedComponents = components.map((c) => ({
      ...c,
      elements: [...c.elements],
    }));

    for (const [i, comp] of repairedComponents.entries()) {
      const booleanEl = elementMap.get(comp.id);
      if (booleanEl?.type !== 'boolean') continue;
      if (comp.elements.includes(comp.id)) continue;

      comp.elements = [comp.id, ...comp.elements];

      for (const [j, other] of repairedComponents.entries()) {
        if (j === i) continue;
        if (other.elements.includes(comp.id)) {
          other.elements = other.elements.filter((id) => id !== comp.id);
        }
      }
    }

    // Fix 2: reconstruct renderedById on child elements

    const repairedElements = elements.map((el) => ({ ...el }));

    const repairedElementMap = new Map(
      repairedElements.map((el) => [el.id, el]),
    );

    for (const component of repairedComponents) {
      const booleanId = component.elements.find(
        (id) => repairedElementMap.get(id)?.type === 'boolean',
      );
      if (!booleanId) continue;

      for (const elementId of component.elements) {
        const el = repairedElementMap.get(elementId);
        if (!el) continue;
        if (!('points' in el)) continue;
        if (NON_BOOLEAN_TYPES.has(el.type)) continue;
        if (el.renderedById === booleanId) continue;

        el.renderedById = booleanId;
      }
    }

    return {
      elements: repairedElements,
      components: repairedComponents,
      ...rest,
    };
  });

  const basisIds = new Set(repairedViews[0]?.components.map((c) => c.id));

  for (const [viewIndex, view] of repairedViews.entries()) {
    const seenGroups = new Set<string>();

    view.components = view.components.map((component) => {
      const { groupId } = component;
      // View 0 can only reference itself, other views a basis component,
      // at most once per view.
      const isValid =
        groupId &&
        (viewIndex === 0
          ? groupId === component.id
          : basisIds.has(groupId) && !seenGroups.has(groupId));

      if (isValid) seenGroups.add(groupId);

      return isValid ? component : { ...component, groupId: undefined };
    });
  }

  return {
    ...rest,
    views: repairedViews,
  };
};
